import React, { useState, useRef, useEffect } from 'react';
import { useTeamDashboard } from '../TeamDashboardContext';
import type { PageSlug } from '../types';

/** Aisha presence badge — shows when the AI model is actively processing. */
function AishaTeamPresence() {
  const [active, setActive] = useState(false);
  const [activity, setActivity] = useState('');

  useEffect(() => {
    // Listen for AI activity events from the cross-dashboard bridge
    const onAiActivity = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.active) {
        setActive(true);
        setActivity(detail.activity || 'thinking');
        const timer = setTimeout(() => setActive(false), 30_000);
        return () => clearTimeout(timer);
      }
      setActive(false);
    };
    window.addEventListener('aisha:activity', onAiActivity);
    return () => window.removeEventListener('aisha:activity', onAiActivity);
  }, []);

  if (!active) return null;

  return (
    <div
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-bold font-mono tracking-wider border shrink-0 animate-in fade-in"
      style={{
        background: 'rgba(139, 92, 246, 0.08)',
        borderColor: 'rgba(139, 92, 246, 0.2)',
        color: '#8b5cf6',
      }}
      title="Aisha — the AI model powering Lumi — is actively working"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse" />
      <span>Aisha is {activity}</span>
    </div>
  );
}

const PAGE_TITLES: Record<PageSlug, { section: string; title: string }> = {
  overview: { section: 'Workspace', title: 'Overview' },
  team: { section: 'Workspace', title: 'Team' },
  budget: { section: 'Workspace', title: 'Budget' },
  quality: { section: 'Workspace', title: 'Quality' },
  projects: { section: 'Workspace', title: 'Projects' },
  invoices: { section: 'Workspace', title: 'Invoices' },
  devmode: { section: 'Development', title: 'Dev Mode' },
  reports: { section: 'Development', title: 'Reports' },
  workclock: { section: 'Work Clock', title: 'Live Overview' },
  wcshifts: { section: 'Work Clock', title: 'Assign Shifts' },
  wccommits: { section: 'Work Clock', title: 'Commit Feed' },
  wcbudget: { section: 'Work Clock', title: 'Budget Live' },
  discussion: { section: 'Collaboration', title: 'Discussion Room' },
  settings: { section: 'Collaboration', title: 'Settings' },
};

const DEMO_NOTIFS = [
  { id: '1', type: 'red' as const, title: 'Budget alert', sub: 'Auth Service at 88% budget used', timestamp: '5m ago', read: false },
  { id: '2', type: 'green' as const, title: 'PR merged', sub: 'Arjun merged PR #14 — auth.js', timestamp: '32m ago', read: false },
  { id: '3', type: 'amber' as const, title: 'Work clock', sub: 'Rahul has been working for 6h 30m', timestamp: '1h ago', read: true },
  { id: '4', type: 'blue' as const, title: 'New commit', sub: 'Priya pushed to main — 3 files changed', timestamp: '2h ago', read: true },
];

export default function Topbar() {
  const { activePage, isReal, toggleReal, burnActive, burnAmount, setActivePage, isManager, toggleSidebar } = useTeamDashboard();
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState(DEMO_NOTIFS);
  const notifRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);

  const unread = notifs.filter(n => !n.read).length;
  const { section, title } = PAGE_TITLES[activePage];

  useEffect(() => {
    function onClickOut(e: MouseEvent) {
      if (
        notifRef.current && !notifRef.current.contains(e.target as Node) &&
        bellRef.current && !bellRef.current.contains(e.target as Node)
      ) {
        setNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  function markAllRead() {
    setNotifs(n => n.map(x => ({ ...x, read: true })));
  }

  function notifColor(type: string) {
    if (type === 'green') return 'var(--green)';
    if (type === 'red') return 'var(--red)';
    if (type === 'amber') return 'var(--amber)';
    return 'var(--blue)';
  }

  return (
    <div className="td-topbar">
      <div className="flex items-center gap-2">
        {/* Mobile menu hamburger toggle */}
        <button
          type="button"
          className="td-mobile-menu-btn"
          onClick={toggleSidebar}
          aria-label="Toggle team navigation"
          title="Open menu"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>

        {/* Breadcrumb */}
        <div className="bc">
          <span>{section}</span>
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
            <path d="M2 1l3 3-3 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <b>{title}</b>
        </div>
      </div>

      {/* Right side controls */}
      <div className="tbr">
        {/* Burn ticker */}
        <div className={`burn-ticker${burnActive ? ' active-burn' : ''}`}>
          <div className="bt-dot" />
          <span className="bt-val">{burnActive ? burnAmount : '₹0'}</span>
          <span className="bt-label">/hr burn</span>
        </div>

        <div className="tbdiv" />

        {/* Live Sync WebSocket Connectivity Badge */}
        <div 
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-bold font-mono tracking-wider transition-all select-none border shrink-0"
          style={{
            background: isReal ? 'rgba(16, 185, 129, 0.08)' : 'rgba(245, 158, 11, 0.08)',
            borderColor: isReal ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)',
            color: isReal ? '#10b981' : '#f59e0b',
            boxShadow: isReal ? '0 0 10px rgba(16, 185, 129, 0.1)' : 'none'
          }}
          title={isReal ? 'WebSocket stream active' : 'Running mock client database'}
        >
          <span 
            className={`w-1.5 h-1.5 rounded-full ${isReal ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} 
            style={{
              boxShadow: isReal ? '0 0 6px #10b981' : 'none'
            }}
          />
          <span>{isReal ? 'LIVE SYNCED' : 'DEMO MODE'}</span>
        </div>

        <div className="tbdiv" />

        {/* Aisha presence — shows when the AI model is actively working */}
        <AishaTeamPresence />

        <div className="tbdiv" />

        {/* Real/Demo toggle */}
        <button
          className={`real-toggle${isReal ? ' active' : ''}`}
          onClick={toggleReal}
          title={isReal ? 'Switch to Demo mode' : 'Switch to Real mode'}
        >
          <div className="rtd" />
          {isReal ? 'Real' : 'Demo'}
        </button>

        <div className="tbdiv" />

        {/* Notifications bell */}
        <div style={{ position: 'relative' }}>
          <button
            ref={bellRef}
            className={`notif-bell-btn${unread > 0 ? ' has-notif' : ''}`}
            onClick={() => setNotifOpen(o => !o)}
            aria-label="Notifications"
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
              <path d="M7 1.5a4.5 4.5 0 0 1 4.5 4.5v2.5l1 1.5H1.5L2.5 8.5V6A4.5 4.5 0 0 1 7 1.5z" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M5.5 10.5a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.2"/>
            </svg>
            <span className="notif-badge">{unread}</span>
          </button>

          <div
            ref={notifRef}
            className={`notif-dropdown${notifOpen ? ' open' : ''}`}
            style={{ top: '38px', right: 0 }}
          >
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)' }}>Notifications</span>
              {unread > 0 && (
                <button onClick={markAllRead} style={{ fontSize: '10px', fontFamily: 'var(--mono)', color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer' }}>
                  Mark all read
                </button>
              )}
            </div>
            {notifs.map(n => (
              <div key={n.id} className={`notif-item${!n.read ? ' unread' : ''}`} onClick={() => setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x))}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: notifColor(n.type), flexShrink: 0, marginTop: 4 }} />
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div className="notif-body-title">{n.title}</div>
                  <div className="notif-body-sub">{n.sub}</div>
                  <div className="notif-time">{n.timestamp}</div>
                </div>
                {!n.read && <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--blue)', flexShrink: 0, marginTop: 6 }} />}
              </div>
            ))}
          </div>
        </div>

        {/* Invite CTA */}
        {isManager && (
          <button className="ctabtn" onClick={() => setActivePage('team')}>
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
              <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            Invite
          </button>
        )}
      </div>
    </div>
  );
}
